import moment from 'moment';
import { effectiveHoursOfDay, isDayCoarse, unitKey, seedGraceFor, graceUnitFor, ordinal, LAST_DAY_OF_MONTH } from 'helpers/recurrenceLimits';
import { getZonedCalendarDate, getBrowserTimeZone } from 'helpers/dateTimeHelper';
import { formatHour } from './CadenceEditor';

var DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

function joinList(items, t) {
  if (items.length <= 1) return items.join('');
  return items.slice(0, -1).join(', ') + ' ' + t('inspect:summaryAnd') + ' ' + items[items.length - 1];
}

// "Every week on Mon, Wed and Fri" -- the cadence alone, the dialog's headline.
export function cadenceHeadline(r, t) {
  var interval = r.interval || 1;
  var unit = { daily: 'days', weekly: 'weeks', monthly: 'months', yearly: 'years' }[r.frequency] || 'days';
  var every = interval === 1
    ? t('inspect:summaryEveryOne', { unit: t(unitKey(unit, 1)) })
    : t('inspect:summaryEveryN', { count: interval, unit: t(unitKey(unit, interval)) });
  var on = '';
  if (r.frequency === 'weekly') {
    var days = DAY_ORDER.filter(function (d) { return (r.daysOfWeek || []).indexOf(d) !== -1; })
      .map(function (d) { return t('inspect:dow_' + d); });
    if (days.length) on = t('inspect:summaryOnDays', { days: joinList(days, t) });
  } else if (r.frequency === 'monthly') {
    // -1 is the last day and sorts after every real day of the month.
    var doms = (r.daysOfMonth || []).slice().sort(function (a, b) { return (a === LAST_DAY_OF_MONTH ? 32 : a) - (b === LAST_DAY_OF_MONTH ? 32 : b); })
      .map(function (d) { return d === LAST_DAY_OF_MONTH ? t('inspect:summaryLastDay') : t('inspect:summaryTheNth', { nth: ordinal(d) }); });
    if (doms.length) on = t('inspect:summaryOnDays', { days: joinList(doms, t) });
  } else if (r.frequency === 'yearly' && r.startDate) {
    on = t('inspect:summaryOnDays', {
      days: moment(getZonedCalendarDate(r.startDate, r.timeZone || getBrowserTimeZone())).format('MMMM Do'),
    });
  }
  return on ? every + ' ' + on : every;
}

function dueLine(r, t) {
  var hours = effectiveHoursOfDay(r.frequency, r.hoursOfDay);
  return isDayCoarse(hours)
    ? t('inspect:summaryByEndOfDay')
    : t('inspect:summaryAtTimes', { times: joinList(hours.slice().sort(function (a, b) { return a - b; }).map(formatHour), t) });
}

function startsText(r, t) {
  var anchor = r.firstDueAnchor || 'today';
  if (anchor === 'date') {
    if (!r.firstDueDate) return t('inspect:summaryStartsDateMissing');
    var day = getZonedCalendarDate(r.firstDueDate, r.timeZone);
    return t('inspect:summaryStartsDate', { date: moment(day).format('MMM D, YYYY') });
  }
  if (anchor === 'field') {
    var offset = r.firstDueOffsetInterval > 0
      ? ' ' + t('inspect:summaryPlusOffset', { count: r.firstDueOffsetInterval, unit: t(unitKey(r.firstDueOffsetUnit || 'days', r.firstDueOffsetInterval)) })
      : '';
    return t('inspect:summaryStartsField', { field: r.firstDueFieldTitle || t('inspect:summaryUnnamedField'), offset: offset });
  }
  return t('inspect:summaryStartsToday');
}

// Plain-English restatement of a recurrence config for the save confirmation: a headline (the
// cadence), a subline (when in the day it is due, and in which zone), one sentence per rule
// (`rows`, each carrying its Material Symbol), and the On Save choice as its own block.
// Every value is read from the same config the save sends, so it can never disagree with the
// form. `status` is the schedule-status count (may be null) and `recompute` the On Save choice.
export function describeRecurrence(recurrence, status, recompute, t) {
  var r = recurrence || {};
  var mode = r.mode || 'none';
  if (mode === 'none') return { headline: t('inspect:summaryModeNone'), subline: '', rows: [], onSave: null };
  if (mode === 'manual') {
    return {
      headline: t('inspect:summaryModeManual', { field: r.fieldTitle || t('inspect:summaryUnnamedField') }),
      subline: '',
      rows: [],
      onSave: null,
    };
  }
  var rows = [];
  // An untouched window is not stored: the save sends null and the API seeds the same default
  // seedGraceFor shows in the form, so the row reports what will actually be saved.
  var grace = r.graceIsCustom
    ? { value: r.graceInterval, unit: graceUnitFor(effectiveHoursOfDay(r.frequency, r.hoursOfDay)) }
    : seedGraceFor(r);
  if (grace.value > 0) {
    rows.push({ key: 'window', icon: 'schedule', text: t('inspect:summaryGrace', { count: grace.value, unit: t(unitKey(grace.unit, grace.value)) }) });
  }
  rows.push({ key: 'firstDue', icon: 'flag', text: startsText(r, t) });
  var onSave = null;
  if (status && status.withDueDateCount > 0) {
    onSave = recompute
      ? { title: t('inspect:summaryOnSaveRecompute'), text: t('inspect:summaryRecomputeAll', { count: status.templateCount }) }
      : {
        title: t('inspect:summaryOnSaveKeep'),
        text: t('inspect:summaryKeepCurrent', { count: status.withDueDateCount, total: status.templateCount }),
      };
  }
  var subline = r.timeZone ? t('inspect:summaryDueInZone', { due: dueLine(r, t), zone: r.timeZone }) : dueLine(r, t);
  return { headline: cadenceHeadline(r, t), subline: subline, rows: rows, onSave: onSave };
}
